import React, { useState } from "react";
import { toast } from "react-toastify";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { getAppConfig } from "../../../utils/appConfig";
import api, { API_BASE } from "../../../utils/api";
import { getLocalizedName } from "../../../utils/localizedText";

/**
 * Product Table Component
 * Displays selected products with quantity controls, prices, and remove actions
 * Shows empty state when no products are added
 *
 * @param {Object} props
 * @param {Array} props.products - Array of product objects in the order
 * @param {Function} props.updateQuantity - Function to update product quantity (id, qty)
 * @param {Function} props.removeProduct - Function to remove product from order (id)
 * @param {Function} props.updateProductAddOns - Function to change a product's add-ons (id, chosenAddOns, availableAddOns)
 * @param {Function} props.updateProductNote - Function to set a product's note (id, note)
 */
const ProductTable = ({ products, updateQuantity, removeProduct, updateProductAddOns, updateProductNote }) => {
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  const { defaultImage } = getAppConfig();

  const [editingProduct, setEditingProduct] = useState(null);
  // Map of addOnId -> chosen quantity (0/absent = not selected).
  const [pickerQuantities, setPickerQuantities] = useState({});
  const [loadingAddOnsFor, setLoadingAddOnsFor] = useState(null);

  const visibleAddOns = (list) => (list || []).filter((a) => a.isVisible !== false);

  const openAddOnsEditor = async (p) => {
    setPickerQuantities(
      Object.fromEntries((p.addOns || []).map((a) => [a.addOnId, a.quantity || 1])),
    );

    if (p.availableAddOns) {
      setEditingProduct(p);
      return;
    }

    setLoadingAddOnsFor(p._id);
    try {
      const res = await api.get(`/products/${p._id}`);
      setEditingProduct({ ...p, availableAddOns: res.data?.addOns || [] });
    } catch (err) {
      console.error("Failed to load add-ons", err);
      toast.error("Failed to load add-ons for this product");
    } finally {
      setLoadingAddOnsFor(null);
    }
  };

  const setPickerAddOnQty = (addOnId, qty) => {
    setPickerQuantities((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[addOnId];
        return next;
      }
      return { ...prev, [addOnId]: qty };
    });
  };

  const confirmAddOnPicker = () => {
    const chosen = visibleAddOns(editingProduct.availableAddOns)
      .filter((a) => (pickerQuantities[a._id] || 0) > 0)
      .map((a) => ({
        addOnId: a._id,
        name: getLocalizedName(a.name),
        price: Number(a.price),
        quantity: pickerQuantities[a._id],
      }));
    updateProductAddOns(editingProduct._id, chosen, editingProduct.availableAddOns);
    setEditingProduct(null);
  };
  const getImageSrc = (product) => {
    const path = product.image || product.imageUrl;

    if (path) {
      if (path.startsWith("http")) return path;
      return `${API_BASE}${path}`;
    }

    if (defaultImage) {
      return `${API_BASE}${defaultImage}`;
    }

    return "/img/finglider_logo.jpg";
  };

  if (!products.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg
          className="w-16 h-16 mx-auto mb-3 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <p className="text-sm">No products added yet</p>
        <p className="text-xs text-gray-400 mt-1">
          Search and select products to add them here
        </p>
      </div>
    );
  }

  return (
    <>
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[600px] sm:min-w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-1.5 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700">
              Product
            </th>
            <th className="text-right py-1.5 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
              Price
            </th>
            <th className="text-center py-1.5 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
              Qty
            </th>
            <th className="text-right py-1.5 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
              Total
            </th>
            <th className="py-1.5 px-1 sm:px-2"></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr
              key={p._id}
              className="border-b border-gray-100 hover:bg-gray-50"
            >
              {/* Product Name & Image */}
              <td className="py-1.5 px-1 sm:px-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <img
                    src={getImageSrc(p)}
                    alt={p.productName}
                    className="w-6 h-6 sm:w-8 sm:h-8 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                      e.target.src = defaultImage
                        ? `${API_BASE}${defaultImage}`
                        : "/img/finglider_logo.jpg";
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                      {p.productName}
                    </div>
                    {p.packageCharge > 0 && (
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        +{currencySymbol}
                        {p.packageCharge} pkg
                      </div>
                    )}
                    {p.addOns?.length > 0 && (
                      <div className="text-[10px] sm:text-xs text-indigo-600 truncate">
                        +{" "}
                        {p.addOns
                          .map((a) => (a.quantity > 1 ? `${a.name} x${a.quantity}` : a.name))
                          .join(", ")}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-0.5">
                      {updateProductAddOns && (
                        <button
                          type="button"
                          onClick={() => openAddOnsEditor(p)}
                          disabled={loadingAddOnsFor === p._id}
                          className="text-[10px] sm:text-xs text-indigo-600 hover:underline disabled:opacity-50 whitespace-nowrap flex-shrink-0"
                        >
                          {loadingAddOnsFor === p._id
                            ? "Loading..."
                            : p.addOns?.length > 0
                              ? "Edit add-ons"
                              : "Add-ons"}
                        </button>
                      )}
                      {updateProductNote && (
                        <input
                          type="text"
                          value={p.note || ""}
                          onChange={(e) => updateProductNote(p._id, e.target.value)}
                          placeholder="Add note..."
                          maxLength={500}
                          className="min-w-0 flex-1 text-[10px] sm:text-xs text-gray-600 placeholder-gray-400 bg-transparent border-b border-dashed border-gray-300 focus:border-indigo-400 outline-none"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </td>

              {/* Price */}
              <td className="py-1.5 px-1 sm:px-2 text-right whitespace-nowrap">
                <div className="text-xs sm:text-sm font-medium text-gray-900">
                  {currencySymbol}
                  {p.finalPrice}
                </div>
                {p.offerPrice && (
                  <div className="text-[10px] sm:text-xs text-gray-400 line-through">
                    {currencySymbol}
                    {p.price}
                  </div>
                )}
              </td>

              {/* Quantity */}
              <td className="py-1.5 px-1 sm:px-2">
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <button
                    onClick={() =>
                      updateQuantity(p._id, Math.max(1, p.quantity - 1))
                    }
                    className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-600 font-semibold text-xs sm:text-sm"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={p.quantity === "" ? "" : p.quantity}
                    min="1"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        updateQuantity(p._id, "");
                        return;
                      }
                      const qty = parseInt(value);
                      updateQuantity(p._id, isNaN(qty) ? 0 : qty);
                    }}
                    className="w-9 sm:w-11 text-center border border-gray-300 rounded py-0 sm:py-0.5 text-xs sm:text-sm"
                  />
                  <button
                    onClick={() => updateQuantity(p._id, p.quantity + 1)}
                    className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-600 font-semibold text-xs sm:text-sm"
                  >
                    +
                  </button>
                </div>
              </td>

              {/* Total */}
              <td className="py-1.5 px-1 sm:px-2 text-right whitespace-nowrap">
                <div className="text-xs sm:text-sm font-semibold text-gray-900">
                  {currencySymbol}
                  {(p.quantity * p.finalPrice).toFixed(2)}
                </div>
              </td>

              {/* Remove Button */}
              <td className="py-1.5 px-1 sm:px-2 text-center">
                <button
                  onClick={() => removeProduct(p._id)}
                  className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove item"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Add-on Picker */}
    {editingProduct && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">
              Edit add-ons for {editingProduct.productName}
            </h3>
          </div>
          <div className="px-6 py-4 max-h-[300px] overflow-y-auto space-y-1">
            {visibleAddOns(editingProduct.availableAddOns).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No add-ons available for this product.
              </p>
            ) : (
              visibleAddOns(editingProduct.availableAddOns).map((addOn) => {
                const qty = pickerQuantities[addOn._id] || 0;
                return (
                  <div
                    key={addOn._id}
                    className="flex items-center justify-between py-2 border-b border-gray-100"
                  >
                    <div>
                      <div className="text-sm text-gray-800">{getLocalizedName(addOn.name)}</div>
                      <div className="text-xs text-gray-500">
                        {currencySymbol}
                        {Number(addOn.price).toFixed(2)} each
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPickerAddOnQty(addOn._id, qty - 1)}
                        disabled={qty === 0}
                        className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="text-sm font-semibold min-w-[16px] text-center">{qty}</span>
                      <button
                        type="button"
                        onClick={() => setPickerAddOnQty(addOn._id, qty + 1)}
                        className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-100"
                      >
                        <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
            <button
              onClick={() => setEditingProduct(null)}
              className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmAddOnPicker}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-md transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProductTable;
